import useSWR from 'swr';
import { get } from 'utils/axios';
import {
  GetListingV3PublicEndpoint,
  ExperienceVersion,
  LanguageCode,
} from '@kouto/types';
import { PublicListingDetailsV3 } from 'types/listingV3';
import useSupportedLanguage from 'utils/data/language/useSupportedLanguage';

type UseListingsByIdsV3Props = {
  ids: string[];
  version?: ExperienceVersion;
  language?: LanguageCode;
};

const useListingsByIdsV3 = ({
  ids,
  version = ExperienceVersion.PUBLISHED,
  language,
}: UseListingsByIdsV3Props) => {
  const defaultLanguage = useSupportedLanguage();
  const languageCode = language ?? defaultLanguage;

  const key =
    ids.length > 0
      ? JSON.stringify({
          ids: Array.from(new Set(ids)),
          version,
          language: languageCode,
        })
      : null;

  const { data, isLoading, error } = useSWR<{
    listings: Record<string, PublicListingDetailsV3>;
    failedIds: string[];
  }>(key, async (keyStr: string) => {
    const { ids } = JSON.parse(keyStr);

    // Use allSettled to handle individual failures
    const results = await Promise.allSettled(
      ids.map((id: string) =>
        get<{ data: PublicListingDetailsV3 }>(
          GetListingV3PublicEndpoint.url({
            listingId: id,
            query: {
              version,
              language: languageCode,
            },
          }),
        ).then((result) => ({ id, data: result.data })),
      ),
    );

    const listings: Record<string, PublicListingDetailsV3> = {};
    const failedIds: string[] = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        listings[result.value.data.id] = result.value.data;
      } else {
        // Check if it's a 404 error
        const is404 = result.reason?.response?.status === 404;
        if (is404) {
          failedIds.push(ids[index]);
          console.warn(
            `Listing ${ids[index]} returned 404, will be removed from cart`,
          );
        }
      }
    });

    return { listings, failedIds };
  });

  return {
    listings: data?.listings ?? {},
    isLoading,
    error,
    failedListingIds: data?.failedIds ?? [],
  };
};

export default useListingsByIdsV3;
